import {
    PDFArray,
    PDFBool,
    PDFCheckBox,
    PDFDict,
    PDFDocument,
    PDFDropdown,
    PDFForm,
    PDFHexString,
    PDFName,
    PDFObject,
    PDFOptionList,
    PDFPage,
    PDFRadioGroup,
    PDFRef,
    PDFSignature,
    PDFString,
    PDFTextField,
    PDFWidgetAnnotation,
} from 'pdf-lib';

export type PdfFieldValues = Record<string, string | boolean>;

export interface SignatureStamp {
    fieldName: string;
    /** A "data:image/png;base64,..." URL captured from the signature pad. */
    pngDataUrl: string;
}

/**
 * Apply the patient's answers onto a fillable AcroForm: strings go to text
 * fields, booleans tick/untick checkboxes. Unknown field names and type
 * mismatches are ignored so a sponsor PDF with unexpected fields never crashes
 * the signing flow.
 */
export const applyFieldValues = (form: PDFForm, values: PdfFieldValues): void => {
    const byName = new Map(form.getFields().map((f) => [f.getName(), f]));
    Object.entries(values).forEach(([name, value]) => {
        const field = byName.get(name);
        if (!field) return;

        if (field instanceof PDFCheckBox) {
            const checked =
                typeof value === 'boolean' ? value : value !== '' && value !== 'Off';
            if (checked) field.check();
            else field.uncheck();
            return;
        }
        if (field instanceof PDFRadioGroup) {
            // The export value of the chosen option; ignore unknown/blank states.
            if (typeof value === 'string' && value && value !== 'Off') {
                try {
                    field.select(value);
                } catch {
                    // Option not defined by this group (mismatched export value);
                    // leave unselected rather than breaking the signing flow.
                }
            }
            return;
        }
        if (field instanceof PDFDropdown || field instanceof PDFOptionList) {
            if (typeof value === 'string' && value) {
                try {
                    field.select(value);
                } catch {
                    // Not a defined option; ignore rather than crash the flow.
                }
            }
            return;
        }
        if (field instanceof PDFTextField)
            field.setText(typeof value === 'boolean' ? '' : value);
    });
};

export interface PdfAnnotation {
    id: string;
    fieldName?: string;
    fieldType?: string;
    /**
     * The on-state export value of a Btn widget (pdf.js `buttonValue` for radios,
     * `exportValue` for checkboxes). pdf.js stores a selected radio as boolean `true`,
     * so this is the only way to recover which option ("Yes"/"No") was chosen.
     */
    exportValue?: string;
}

/**
 * Turn pdf.js's annotation storage (keyed by annotation id) into a field-name →
 * value map suitable for {@link fillAndFlattenPdf}. Boolean storage values are
 * checkbox/radio states; everything else is coerced to text. Annotations without
 * a field name, or with no storage entry (untouched fields), are skipped.
 */
export const extractPdfFieldValues = (
    annotations: PdfAnnotation[],
    storageAll: Record<string, { value: unknown }>,
): PdfFieldValues => {
    const values: PdfFieldValues = {};
    annotations.forEach((annotation) => {
        const { id, fieldName, fieldType, exportValue } = annotation;
        if (!fieldName) return;
        const entry = storageAll[id];
        if (!entry) return;
        const raw = entry.value;
        if (fieldType === 'Btn') {
            // pdf.js stores a checkbox/radio as a boolean or as its export value
            // string ('Off' meaning unchecked). A radio group has several widgets
            // sharing one field name; keep the chosen option's export value (so it
            // can be re-selected and so conditional rules can key off it) and never
            // let an unselected 'Off' sibling overwrite an already-recorded selection.
            // pdf.js records the selected radio as boolean `true`, so fall back to the
            // widget's own export value to recover which option was chosen.
            const isOn =
                typeof raw === 'boolean'
                    ? raw
                    : raw != null && raw !== 'Off' && raw !== '';
            if (isOn) {
                values[fieldName] =
                    typeof raw === 'string'
                        ? raw
                        : exportValue && exportValue !== 'Off'
                          ? exportValue
                          : true;
            } else if (!(fieldName in values)) values[fieldName] = false;
            return;
        }
        values[fieldName] = typeof raw === 'boolean' ? raw : String(raw ?? '');
    });
    return values;
};

const STAFF_SIGNATURE_HINTS = [
    'staff',
    'physician',
    'hcp',
    'investigator',
    'witness',
    'clinician',
];
const PATIENT_SIGNATURE_HINTS = ['participant', 'patient', 'subject'];
// "initial"/"initials" (incl. yes/no pairs) and the French "paraphe".
const INITIALS_NAME_HINTS = ['initial', 'paraph', 'parafe'];

const includesHint = (name: string, hints: string[]): boolean =>
    hints.some((hint) => name.toLowerCase().includes(hint));

/** Whether a field name reads as a staff/clinician signature field. */
export const isStaffSignatureFieldName = (name: string): boolean =>
    includesHint(name, STAFF_SIGNATURE_HINTS);

/** Whether a field name reads as a patient/participant signature field. */
export const isPatientSignatureFieldName = (name: string): boolean =>
    includesHint(name, PATIENT_SIGNATURE_HINTS);

/** Whether a field name reads as an initials field (e.g. initials_yes, paraphe). */
export const isInitialsFieldName = (name: string): boolean =>
    includesHint(name, INITIALS_NAME_HINTS);

/**
 * Choose which signature field the patient signs, across sponsor PDFs that name
 * their fields differently. Prefer an explicitly patient/participant field, then
 * any field that is not clearly a staff/physician field, then the first one.
 */
export const pickPatientSignatureFieldName = (
    fieldNames: string[],
): string | null =>
    fieldNames.find(isPatientSignatureFieldName) ??
    fieldNames.find((name) => !isStaffSignatureFieldName(name)) ??
    fieldNames[0] ??
    null;

/**
 * Choose which signature field the clinician counter-signs: prefer an explicit
 * staff/physician field, otherwise the first field that is not the patient's.
 * Returns null when the only signature field is the patient's.
 */
export const pickStaffSignatureFieldName = (
    fieldNames: string[],
): string | null =>
    fieldNames.find(isStaffSignatureFieldName) ??
    fieldNames.find((name) => !isPatientSignatureFieldName(name)) ??
    null;

/**
 * Initials targets for one signer, detected the same robust way as the signature
 * field: every field whose name reads as initials (initials_yes / initials_no /
 * paraphe), plus any signature(/Sig) widget — sponsor PDFs draw the initials box
 * either as a named field or as a second, smaller signature widget. Pass the
 * chosen signature field and the other party's signature fields in
 * `excludeFieldNames` so they are never offered as initials targets.
 */
export const pickInitialsFieldNames = (
    allFieldNames: string[],
    signatureFieldNames: string[],
    excludeFieldNames: string[] = [],
): string[] => {
    const exclude = new Set(excludeFieldNames);
    const signatureSet = new Set(signatureFieldNames);
    return allFieldNames.filter(
        (name) =>
            !exclude.has(name) &&
            (isInitialsFieldName(name) || signatureSet.has(name)),
    );
};

export interface FieldRect {
    pageIndex: number;
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface OverlayBox {
    left: number;
    top: number;
    width: number;
    height: number;
}

/**
 * Convert a field rect in PDF points (bottom-left origin) into a top-left CSS
 * box for a page rendered at `scale`, given the rendered page height in px.
 */
export const pdfRectToOverlayBox = (
    rect: FieldRect,
    viewportHeightPx: number,
    scale: number,
): OverlayBox => ({
    left: rect.x * scale,
    top: viewportHeightPx - (rect.y + rect.height) * scale,
    width: rect.width * scale,
    height: rect.height * scale,
});

/**
 * Return PDF bytes with the named AcroForm fields marked read-only, so pdf.js
 * renders them non-editable (used to lock HCP/display fields away from the patient).
 */
export const applyReadOnly = async (
    pdfBytes: ArrayBuffer | Uint8Array,
    readOnlyFieldNames: string[],
): Promise<Uint8Array> => {
    const doc = await PDFDocument.load(pdfBytes);
    const lock = new Set(readOnlyFieldNames);
    doc.getForm()
        .getFields()
        .forEach((field) => {
            if (lock.has(field.getName())) field.enableReadOnly();
        });
    return doc.save();
};

export type PdfFieldType =
    | 'text'
    | 'checkbox'
    | 'signature'
    | 'dropdown'
    | 'optionList'
    | 'radio'
    | 'unknown';

export interface PdfFieldInfo {
    name: string;
    type: PdfFieldType;
    /** The PDF's own AcroForm Required flag — the source of truth for validation. */
    required: boolean;
}

const fieldType = (field: unknown): PdfFieldType => {
    if (field instanceof PDFTextField) return 'text';
    if (field instanceof PDFCheckBox) return 'checkbox';
    if (field instanceof PDFSignature) return 'signature';
    if (field instanceof PDFDropdown) return 'dropdown';
    if (field instanceof PDFOptionList) return 'optionList';
    if (field instanceof PDFRadioGroup) return 'radio';
    return 'unknown';
};

/** Every AcroForm field with its type, in document order. */
export const listFields = async (
    pdfBytes: ArrayBuffer | Uint8Array,
): Promise<PdfFieldInfo[]> => {
    const doc = await PDFDocument.load(pdfBytes);
    return doc
        .getForm()
        .getFields()
        .map((field) => ({
            name: field.getName(),
            type: fieldType(field),
            required: field.isRequired(),
        }));
};

/**
 * Whether the fields include at least one signature field — the minimum a
 * fillable-PDF consent needs so the patient has somewhere to sign.
 */
export const hasSignatureField = (fields: PdfFieldInfo[]): boolean =>
    fields.some((field) => field.type === 'signature');

const dataUrlToBytes = (dataUrl: string): Uint8Array => {
    const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
};

/**
 * Locate the page a widget is drawn on. Sponsor PDFs often omit the widget's
 * /P back-reference, so fall back to scanning each page's annotations for the
 * widget's own dictionary.
 */
const findWidgetPage = (
    doc: PDFDocument,
    widget: PDFWidgetAnnotation,
): PDFPage => {
    const pages = doc.getPages();
    const widgetDict = widget.dict;
    const context = doc.context;
    const match = pages.find((page) => {
        const annots = page.node.Annots();
        if (!annots) return false;
        for (let i = 0; i < annots.size(); i += 1) 
            if (context.lookup(annots.get(i)) === widgetDict) return true;
        
        return false;
    });
    return match ?? pages[0];
};

export interface InspectedField {
    name: string;
    type: PdfFieldType;
    /** The box of the field's first widget, or null when it has none. */
    rect: FieldRect | null;
    /** The PDF's own AcroForm Required flag — the source of truth for validation. */
    required: boolean;
}

/**
 * Every AcroForm field with its name, type and first-widget rect, resolved from a
 * SINGLE {@link PDFDocument.load}. A signing flow can discover its signature/initials
 * targets and their boxes from one parse instead of re-parsing the document once per
 * helper call and once per field.
 */
export const inspectFillablePdf = async (
    pdfBytes: ArrayBuffer | Uint8Array,
): Promise<InspectedField[]> => {
    const doc = await PDFDocument.load(pdfBytes);
    const pages = doc.getPages();
    return doc
        .getForm()
        .getFields()
        .map((field) => {
            const widget = field.acroField.getWidgets()[0];
            let rect: FieldRect | null = null;
            if (widget) {
                const r = widget.getRectangle();
                rect = {
                    pageIndex: pages.indexOf(findWidgetPage(doc, widget)),
                    x: r.x,
                    y: r.y,
                    width: r.width,
                    height: r.height,
                };
            }
            return {
                name: field.getName(),
                type: fieldType(field),
                rect,
                required: field.isRequired(),
            };
        });
};

/**
 * A field's own conditional-visibility rule, read from the PDF's `/Hide` annotation
 * actions (the mechanism a sponsor PDF uses to gate fields — e.g. an "opt-in" radio
 * that hides the "no" initials when "yes" is chosen). When the {@link controller}
 * field currently holds the {@link on} value, the {@link target} field is hidden
 * ({@link hidden} true) or shown ({@link hidden} false).
 */
export interface PdfHideRule {
    controller: string;
    on: string;
    target: string;
    hidden: boolean;
}

/** The export value (appearance state) a Btn widget carries, e.g. "Yes"; null for non-Btn widgets. */
const widgetOnValue = (widget: PDFWidgetAnnotation): string | null => {
    const ap = widget.dict.lookup(PDFName.of('AP'));
    if (ap instanceof PDFDict) {
        const normal = ap.lookup(PDFName.of('N'));
        if (normal instanceof PDFDict) {
            const key = normal
                .keys()
                .map((k) => k.toString())
                .find((k) => k !== '/Off');
            if (key) return key.replace(/^\//, '');
        }
    }
    return null;
};

/**
 * Every `/Hide` action the PDF attaches to its (radio/checkbox) widgets, resolved to
 * controller/target field names. Sponsor PDFs encode conditional fields this way
 * rather than as admin metadata, so honouring these rules makes the signer follow the
 * document's own validation schema. Action chains (`/Next`) and array/annotation
 * targets are flattened to one rule per (widget value, target).
 */
export const extractHideRules = async (
    pdfBytes: ArrayBuffer | Uint8Array,
): Promise<PdfHideRule[]> => {
    const doc = await PDFDocument.load(pdfBytes);
    const context = doc.context;

    const fieldNameByWidget = new Map<PDFDict, string>();
    doc.getForm()
        .getFields()
        .forEach((field) =>
            field.acroField
                .getWidgets()
                .forEach((w) => fieldNameByWidget.set(w.dict, field.getName())),
        );

    const resolveTargets = (target: PDFRef | PDFObject): string[] => {
        const resolved = context.lookup(target);
        if (resolved instanceof PDFString || resolved instanceof PDFHexString)
            return [resolved.decodeText()];
        if (resolved instanceof PDFArray) {
            const names: string[] = [];
            for (let i = 0; i < resolved.size(); i += 1)
                names.push(...resolveTargets(resolved.get(i)));
            return names;
        }
        if (resolved instanceof PDFDict) {
            const name = fieldNameByWidget.get(resolved);
            return name ? [name] : [];
        }
        return [];
    };

    const rules: PdfHideRule[] = [];
    doc.getForm()
        .getFields()
        .forEach((field) => {
            field.acroField.getWidgets().forEach((widget) => {
                const on = widgetOnValue(widget);
                if (!on) return;
                // Gating lives on either the widget's primary /A action or any of its
                // /AA trigger actions (a sponsor PDF may split show/hide across both).
                const actionRefs: (PDFRef | PDFObject)[] = [];
                const primary = widget.dict.get(PDFName.of('A'));
                if (primary !== undefined) actionRefs.push(primary);
                const aa = widget.dict.lookup(PDFName.of('AA'));
                if (aa instanceof PDFDict)
                    aa.entries().forEach(([, ref]) => actionRefs.push(ref));
                actionRefs.forEach((actionRef) => {
                    const seen = new Set<PDFDict>();
                    let action: PDFObject | undefined | null =
                        context.lookup(actionRef);
                    while (action instanceof PDFDict && !seen.has(action)) {
                        seen.add(action);
                        const kind = action.lookup(PDFName.of('S'));
                        if (kind instanceof PDFName && kind.toString() === '/Hide') {
                            const h = action.lookup(PDFName.of('H'));
                            const hidden = h instanceof PDFBool ? h.asBoolean() : true;
                            const t = action.get(PDFName.of('T'));
                            if (t !== undefined) {
                                resolveTargets(t).forEach((target) =>
                                    rules.push({
                                        controller: field.getName(),
                                        on,
                                        target,
                                        hidden,
                                    }),
                                );
                            }
                        }
                        const next: PDFObject | undefined = action.lookup(
                            PDFName.of('Next'),
                        );
                        action = next ? context.lookup(next) : null;
                    }
                });
            });
        });
    return rules;
};

/**
 * The set of field names currently hidden by the PDF's {@link PdfHideRule}s, given the
 * present field values. A field that some option explicitly *shows* (a `hidden: false`
 * rule) is conditionally-revealed, so it starts hidden until that option is chosen — this
 * is how a sponsor PDF gates a yes/no initials pair behind an opt-in radio. A rule applies
 * when its controller holds its `on` value (the chosen radio export value, or a ticked
 * checkbox); a later "show" overrides an earlier "hide" for the same target.
 */
export const computeHiddenFields = (
    rules: PdfHideRule[],
    values: PdfFieldValues,
): Set<string> => {
    const hidden = new Set<string>(
        rules.filter((rule) => !rule.hidden).map((rule) => rule.target),
    );
    rules.forEach((rule) => {
        const value = values[rule.controller];
        const active =
            value !== undefined &&
            (String(value) === rule.on ||
                (value === true && rule.on !== 'Off'));
        if (!active) return;
        if (rule.hidden) hidden.add(rule.target);
        else hidden.delete(rule.target);
    });
    return hidden;
};

interface ResolvedPlacement {
    page: PDFPage;
    rect: { x: number; y: number; width: number; height: number };
    pngDataUrl: string;
}

const drawImageInRect = async (
    doc: PDFDocument,
    { page, rect, pngDataUrl }: ResolvedPlacement,
): Promise<void> => {
    const png = await doc.embedPng(dataUrlToBytes(pngDataUrl));
    // Fit the signature inside the field box, preserving aspect ratio and centering.
    const scaled = png.scaleToFit(rect.width, rect.height);
    page.drawImage(png, {
        x: rect.x + (rect.width - scaled.width) / 2,
        y: rect.y + (rect.height - scaled.height) / 2,
        width: scaled.width,
        height: scaled.height,
    });
};

/**
 * Fill a sponsor's fillable AcroForm PDF with the patient's answers, then stamp
 * the drawn signature(s) into their field box(es). Signatures are drawn AFTER
 * flatten so neither the flattened field appearances nor an overlapping field
 * (e.g. a printed-name text field sharing the signature box) can cover them.
 * The result is a non-editable, tamper-resistant record of exactly what was signed.
 */
export interface FillOptions {
    /** Flatten the form after filling (default true). Pass false for an intermediate stage. */
    flatten?: boolean;
    /** Field names to mark read-only (e.g. lock the patient's entries before the HCP signs). */
    readOnly?: string[];
}

export const fillAndFlattenPdf = async (
    templateBytes: ArrayBuffer | Uint8Array,
    values: PdfFieldValues,
    signatures: SignatureStamp[] = [],
    options: FillOptions = {},
): Promise<Uint8Array> => {
    const flatten = options.flatten ?? true;
    const readOnly = new Set(options.readOnly ?? []);
    const doc = await PDFDocument.load(templateBytes);
    const form = doc.getForm();

    applyFieldValues(form, values);

    // Resolve each signature's page + box BEFORE flatten (the widgets vanish after).
    const placements: ResolvedPlacement[] = [];
    signatures.forEach((signature) => {
        const field = form
            .getFields()
            .find((f) => f.getName() === signature.fieldName);
        const widget = field?.acroField.getWidgets()[0];
        if (!widget) return;
        const r = widget.getRectangle();
        placements.push({
            page: findWidgetPage(doc, widget),
            rect: { x: r.x, y: r.y, width: r.width, height: r.height },
            pngDataUrl: signature.pngDataUrl,
        });
    });

    form.getFields().forEach((field) => {
        if (readOnly.has(field.getName())) field.enableReadOnly();
    });

    if (flatten) form.flatten();

    // Drawn last so the signature sits on top of the (flattened) field appearances.
    for (const placement of placements)
         
        await drawImageInRect(doc, placement);

    return doc.save();
};
